/**
 * Unified pricing calculator utility
 * Used by both admin calculator and customer quote pages
 * Ensures consistent calculation logic across the application
 */

import apiService from '@/services/api';

export interface PricingData {
  service_id: number | string;
  service_name?: string;
  property_type: string;
  postcode: string;
  urgency: string;
  quantity?: number;
  base_price_pence: number;
}

export interface CalculationResult {
  base_price_pence: number;
  subtotal_pence: number;
  vat_pence: number;
  grand_total_pence: number;
  grand_total_gbp: string;
  applied_rules: any[];
  multiplier: number;
  currency: string;
  breakdown?: {
    basePrice: number;
    zoneAdjusted?: number;
    urgencyAdjusted?: number;
    premiumsTotal?: number;
    finalPrice: number;
  };
}

/**
 * Calculate real-time pricing using current database values
 * This is called by both admin calculator and customer quote pages
 * Ensures consistency and reflects admin changes immediately
 */
export const calculatePrice = async (pricingData: PricingData): Promise<CalculationResult | null> => {
  try {
    console.log('[PricingCalculator] 📊 Calculating price with data:', {
      service_id: pricingData.service_id,
      postcode: pricingData.postcode,
      property_type: pricingData.property_type,
      urgency: pricingData.urgency,
    });

    const response = await apiService.post('/admin/pricing-rules/calculate', {
      service_id: pricingData.service_id,
      service_name: pricingData.service_name || '',
      postcode: pricingData.postcode,
      property_type: pricingData.property_type,
      urgency: pricingData.urgency,
      quantity: pricingData.quantity || 1,
      base_price_pence: pricingData.base_price_pence,
    });

    if (response.success && response.data) {
      console.log('[PricingCalculator] ✅ Price calculated:', {
        total: response.data.grand_total_gbp,
        applied_rules: response.data.applied_rules?.length || 0,
      });

      return response.data as CalculationResult;
    } else {
      console.warn('[PricingCalculator] ⚠️ Calculation failed:', response.message);
      return null;
    }
  } catch (error) {
    console.error('[PricingCalculator] ❌ Error calculating price:', error);
    return null;
  }
};

/**
 * Get pricing breakdown for display
 * Transforms calculation result into a readable format
 */
export const getPricingBreakdown = (calculation: CalculationResult) => {
  if (!calculation) return null;

  return {
    basePrice: (calculation.base_price_pence / 100).toFixed(2),
    subtotal: (calculation.subtotal_pence / 100).toFixed(2),
    vat: (calculation.vat_pence / 100).toFixed(2),
    finalPrice: calculation.grand_total_gbp,
    appliedRules: calculation.applied_rules || [],
    multiplier: calculation.multiplier || 1,
  };
};

/**
 * Format price for display with GBP currency
 */
export const formatPrice = (pricePence: number): string => {
  return (pricePence / 100).toFixed(2);
};

/**
 * Calculate price difference between old and new pricing
 */
export const calculatePriceDifference = (oldPrice: number, newPrice: number) => {
  const difference = newPrice - oldPrice;
  const percentChange = ((difference / oldPrice) * 100).toFixed(1);

  return {
    difference: Math.abs(difference),
    isIncrease: difference > 0,
    percentChange: Math.abs(parseFloat(percentChange)),
    formattedDifference: `$${formatPrice(Math.abs(difference))}`,
  };
};
